import { ChangeEvent } from "react";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";
import { DCUIContent, DCUIContentAcuant, DCUIContentAcuantCamera } from "@iddataweb/idw-common-ui/types";
import { resources } from "@constants/hardcoded-strings";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { DCUIThemingTextContentValidationErrors } from "@idw-types/common/validation/common/dcui-theming-validation-errors";
import { InputFormGroup } from "../../../../../common/input-form-group/input-form-group";
import { TextareaFormGroup } from "../../../../../common/textarea-form-group/textarea-form-group";
import { ThemingFormGroupWrapper } from "@containers/common/theming-input-form-group/theming-form-group-wrapper";
import { PageLevelTypes } from "@enums/page-level-types";

type DCUIThemingTextContentAcuantGeneralSectionProps = {
    layout: LayoutTypes;
    labelWidth: LabelWidthTypes;
    isDisabled: boolean;
    dcuiContent: DCUIContent;
    inheritedDCUIContent: DCUIContent;
    pageLevel: PageLevelTypes;
    validationErrors: DCUIThemingTextContentValidationErrors;
    updateDCUIContentPropertyValue: (dcuiContent: DCUIContent) => void;
};

export const DCUIThemingTextContentAcuantGeneralSection = (props: DCUIThemingTextContentAcuantGeneralSectionProps) => {
    const {
        layout,
        labelWidth,
        isDisabled,
        dcuiContent,
        inheritedDCUIContent,
        pageLevel,
        validationErrors,
        updateDCUIContentPropertyValue
    } = props;

    const updateDCUIContentGeneralAcuantCameraFieldValue = (
        key: keyof DCUIContentAcuantCamera,
        value: string
    ) => {
        const newPropertyValue: DCUIContent = {
            ...dcuiContent,
            acuant: {
                ...dcuiContent.acuant,
                camera: {
                    ...dcuiContent.acuant.camera,
                    [key]: value
                }
            }
        };
        updateDCUIContentPropertyValue(newPropertyValue);
    };

    const updateDCUIContentGeneralAcuantFieldValue = (
        key: keyof DCUIContentAcuant,
        value: string
    ) => {
        const newPropertyValue: DCUIContent = {
            ...dcuiContent,
            acuant: {
                ...dcuiContent.acuant,
                [key]: value,
            },
        };
        updateDCUIContentPropertyValue(newPropertyValue);
    };

    const handleCapturingTextMessageChange = (event: ChangeEvent<HTMLInputElement>) => {
        updateDCUIContentGeneralAcuantCameraFieldValue("capturingText", event.target.value);
    };

    const handleResetCapturingTextMessage = () => {
        updateDCUIContentGeneralAcuantCameraFieldValue("capturingText", inheritedDCUIContent.acuant.camera.capturingText);
    };

    const handleProcessingTextMessageChange = (event: ChangeEvent<HTMLInputElement>) => {
        updateDCUIContentGeneralAcuantCameraFieldValue("processingText", event.target.value);
    };

    const handleResetProcessingTextMessage = () => {
        updateDCUIContentGeneralAcuantCameraFieldValue("processingText", inheritedDCUIContent.acuant.camera.processingText);
    };

    const handleRelaunchCaptureLabelTextChange = (event: ChangeEvent<HTMLInputElement>) => {
        updateDCUIContentGeneralAcuantFieldValue("relaunchCaptureLabelText", event.target.value);
    };

    const handleResetRelaunchCaptureLabelTextText = () => {
        updateDCUIContentGeneralAcuantFieldValue("relaunchCaptureLabelText", inheritedDCUIContent.acuant.relaunchCaptureLabelText);
    };

    const handleRelaunchCaptureButtonTextChange = (event: ChangeEvent<HTMLInputElement>) => {
        updateDCUIContentGeneralAcuantFieldValue("relaunchCaptureButtonText", event.target.value);
    };

    const handleResetRelaunchCaptureButtonText = () => {
        updateDCUIContentGeneralAcuantFieldValue("relaunchCaptureButtonText", inheritedDCUIContent.acuant.relaunchCaptureButtonText);
    };

    const handleCaptureErrorTextChange = (event: ChangeEvent<HTMLInputElement>) => {
        updateDCUIContentGeneralAcuantFieldValue("errorMessage", event.target.value);
    };

    const handleResetCaptureErrorText = () => {
        updateDCUIContentGeneralAcuantFieldValue("errorMessage", inheritedDCUIContent.acuant.errorMessage);
    };

    const generalResources = resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.textContent.sections.general;

    return (
        <div>
            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedDCUIContent.acuant.camera.capturingText}
                currentThemingPropertyValue={dcuiContent.acuant.camera.capturingText}
                pageLevel={pageLevel}
            >
                <InputFormGroup
                    controlId={"document-capture-text-content-capturing-input"}
                    automationId={"document-capture-text-content-capturing-input"}
                    label={generalResources.inputs.capturingText.label}
                    ariaLabel={generalResources.inputs.capturingText.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleCapturingTextMessageChange}
                    value={dcuiContent.acuant.camera.capturingText || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={generalResources.inputs.capturingText.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetCapturingTextMessage}
                />
            </ThemingFormGroupWrapper>
            {validationErrors.capturingTextError}

            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedDCUIContent.acuant.camera.processingText}
                currentThemingPropertyValue={dcuiContent.acuant.camera.processingText}
                pageLevel={pageLevel}
            >
                <InputFormGroup
                    controlId={"document-capture-text-content-processing-input"}
                    automationId={"document-capture-text-content-processing-input"}
                    label={generalResources.inputs.processingText.label}
                    ariaLabel={generalResources.inputs.processingText.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleProcessingTextMessageChange}
                    value={dcuiContent.acuant.camera.processingText || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={generalResources.inputs.processingText.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetProcessingTextMessage}
                />
            </ThemingFormGroupWrapper>
            {validationErrors.processingTextError}
            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedDCUIContent.acuant.relaunchCaptureLabelText}
                currentThemingPropertyValue={dcuiContent.acuant.relaunchCaptureLabelText}
                pageLevel={pageLevel}
            >
                <InputFormGroup
                    controlId={"document-capture-text-content-relaunch-capture-label-input"}
                    automationId={"document-capture-text-content-relaunch-capture-label-input"}
                    label={generalResources.inputs.relaunchCaptureLabelText.label}
                    ariaLabel={generalResources.inputs.relaunchCaptureLabelText.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleRelaunchCaptureLabelTextChange}
                    value={dcuiContent.acuant.relaunchCaptureLabelText || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={generalResources.inputs.relaunchCaptureLabelText.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetRelaunchCaptureLabelTextText}
                />
            </ThemingFormGroupWrapper>
            {validationErrors.relaunchCaptureLabelTextError}
            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedDCUIContent.acuant.relaunchCaptureButtonText}
                currentThemingPropertyValue={dcuiContent.acuant.relaunchCaptureButtonText}
                pageLevel={pageLevel}
            >
                <InputFormGroup
                    controlId={"document-capture-text-content-relaunch-capture-button-input"}
                    automationId={"document-capture-text-content-relaunch-capture-button-input"}
                    label={generalResources.inputs.relaunchCaptureButtonText.label}
                    ariaLabel={generalResources.inputs.relaunchCaptureButtonText.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleRelaunchCaptureButtonTextChange}
                    value={dcuiContent.acuant.relaunchCaptureButtonText || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={generalResources.inputs.relaunchCaptureButtonText.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetRelaunchCaptureButtonText}
                />
            </ThemingFormGroupWrapper>
            {validationErrors.relaunchCaptureButtonTextError}
            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedDCUIContent.acuant.errorMessage}
                currentThemingPropertyValue={dcuiContent.acuant.errorMessage}
                pageLevel={pageLevel}
            >
                <TextareaFormGroup
                    controlId={"document-capture-text-content-capture-error-text-input"}
                    automationId={"document-capture-text-content-capture-error-text-input"}
                    label={generalResources.inputs.captureErrorText.label}
                    ariaLabel={generalResources.inputs.captureErrorText.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleCaptureErrorTextChange}
                    value={dcuiContent.acuant.errorMessage || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={generalResources.inputs.captureErrorText.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetCaptureErrorText}
                />
            </ThemingFormGroupWrapper>
            {validationErrors.captureErrorTextError}
        </div>
    );
};